'use client';

import React from 'react';
import { clsx } from 'clsx';
import {
  Search,
  ArrowDown,
  ArrowUp,
  ArrowDownUp,
  ArrowUpDown,
} from 'lucide-react';
import { SearchClearButton } from '@paeffgen/react/crm-documents/ui';
import { useSearchField } from '@paeffgen/react/crm-documents/util';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import {
  FAVORITES_SORT_OPTIONS,
  type FavoritesSort,
  type FavoritesSortField,
} from './favorites-sort-sheet';

export type { FavoritesSort, FavoritesSortField } from './favorites-sort-sheet';

interface FavoritesToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sort: FavoritesSort;
  onSortChange: (value: FavoritesSort) => void;
  /** Öffnet das Sortier-Sheet (wird von der Page außerhalb des space-y-Containers gerendert). */
  onOpenSort: () => void;
}

export function FavoritesToolbar({
  searchQuery,
  onSearchChange,
  sort,
  onSortChange,
  onOpenSort,
}: FavoritesToolbarProps) {
  const { isAdmin, allCustomers } = useAdmin();
  const bannerVisible = isAdmin && allCustomers;

  // Geteiltes Suchfeld-Verhalten (✕, Escape, Select-all beim Refokus). Der
  // Hook wird EINMAL aufgerufen und auf beide Inputs gespreizt — Mobile-Leiste
  // und Desktop-Karte stehen gleichzeitig im DOM und teilen einen Zustand.
  // Es gibt hier keinen Debounce, also ist onCommit derselbe Weg wie onChange.
  const search = useSearchField({
    value: searchQuery,
    onChange: onSearchChange,
    onCommit: onSearchChange,
  });

  return (
    <>
      {/* Mobile: Full-width Sticky-Leiste analog StickyMobileHeader der Artikelsuche.
          Kein -mt-4, da die Favoriten-Page vor der Toolbar einen eigenen Header hat. */}
      <div
        className={clsx(
          'md:hidden sticky z-30 bg-white border-y border-gray-200 px-4 py-2 -mx-4',
          bannerVisible ? 'top-[6rem]' : 'top-14'
        )}
      >
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 flex-1 min-w-0 h-10 px-3 rounded-lg border border-gray-300 bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
            <Search size={16} className="shrink-0 text-gray-400" />
            <input
              type="text"
              inputMode="search"
              {...search.inputProps}
              placeholder="Suchen in Favoriten..."
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400"
            />
            {search.hasValue && (
              <SearchClearButton
                onClick={search.clear}
                size={14}
                className="shrink-0 -mr-1"
              />
            )}
          </label>

          <button
            type="button"
            onClick={onOpenSort}
            aria-label="Sortierung öffnen"
            aria-haspopup="dialog"
            className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
          >
            <ArrowUpDown size={18} />
          </button>
        </div>
      </div>

      {/* Desktop: bestehender Card-Look, unveraendert */}
      <div className="hidden md:flex bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
        {/* Search */}
        <div className="relative w-full lg:w-80 xl:w-96">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            {...search.inputProps}
            placeholder="Suchen in Favoriten..."
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
          {search.hasValue && (
            <SearchClearButton
              onClick={search.clear}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            />
          )}
        </div>

        {/* Formatting & Actions */}
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto items-stretch sm:items-center">
          {/* Sort: Feldwahl + Richtungs-Toggle (gleiches Modell wie das mobile Sheet) */}
          <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 w-full sm:w-auto">
            <ArrowDownUp size={16} className="text-gray-500 shrink-0" />
            <span className="text-gray-500 whitespace-nowrap hidden md:inline">
              Sortieren:
            </span>
            <select
              value={sort.field}
              onChange={(e) =>
                onSortChange({ ...sort, field: e.target.value as FavoritesSortField })
              }
              className="bg-transparent border-none outline-none font-medium text-gray-700 w-full cursor-pointer focus:ring-0"
            >
              {FAVORITES_SORT_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                onClick={() => onSortChange({ ...sort, direction: 'desc' })}
                aria-label="Absteigend"
                aria-pressed={sort.direction === 'desc'}
                className={clsx(
                  'p-1 rounded transition-colors',
                  sort.direction === 'desc'
                    ? 'bg-white shadow-sm text-primary'
                    : 'text-gray-400 hover:text-gray-600',
                )}
              >
                <ArrowDown size={16} />
              </button>
              <button
                type="button"
                onClick={() => onSortChange({ ...sort, direction: 'asc' })}
                aria-label="Aufsteigend"
                aria-pressed={sort.direction === 'asc'}
                className={clsx(
                  'p-1 rounded transition-colors',
                  sort.direction === 'asc'
                    ? 'bg-white shadow-sm text-primary'
                    : 'text-gray-400 hover:text-gray-600',
                )}
              >
                <ArrowUp size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
